import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const ImagePreviewContext = createContext(null);

export function ImagePreviewProvider({ children }) {
    const [preview, setPreview] = useState(null);

    const openPreview = useCallback((image, alt) => {
        if (!image) return;
        setPreview({ image, alt: alt || '' });
    }, []);

    const closePreview = useCallback(() => setPreview(null), []);

    const value = useMemo(() => ({ openPreview }), [openPreview]);

    return (
        <ImagePreviewContext.Provider value={value}>
            {children}
            <Dialog
                open={!!preview}
                onClose={closePreview}
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        backdropFilter: 'none',
                        border: 'none',
                        overflow: 'visible',
                        m: { xs: 2, sm: 4 },
                    },
                }}
                slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' } } }}
            >
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        onClick={closePreview}
                        aria-label="Cerrar vista previa"
                        sx={{
                            position: 'absolute',
                            top: -16,
                            right: -16,
                            bgcolor: 'rgba(10, 10, 11, 0.85)',
                            color: '#fff',
                            border: '1px solid rgba(255, 255, 255, 0.16)',
                            '&:hover': { bgcolor: 'rgba(10, 10, 11, 1)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {preview && (
                        <Box
                            component="img"
                            src={preview.image}
                            alt={preview.alt}
                            sx={{
                                display: 'block',
                                maxWidth: '90vw',
                                maxHeight: '85vh',
                                width: 'auto',
                                height: 'auto',
                                borderRadius: 2,
                                boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
                            }}
                        />
                    )}
                </Box>
            </Dialog>
        </ImagePreviewContext.Provider>
    );
}

export function useImagePreview() {
    const ctx = useContext(ImagePreviewContext);
    if (!ctx) throw new Error('useImagePreview debe usarse dentro de ImagePreviewProvider');
    return ctx;
}
